import { Injectable, inject } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, filter, map, take } from 'rxjs';
import { PermissionService } from '../auth/permission.service';
import { KeycloakService } from '../auth/keycloak.service';
import { NotifyService } from '../../shared/services/notify.service';
import { I18nService } from '../services/i18n.service';
import { findFirstAccessiblePath } from './first-accessible-route';

/**
 * PermissionGuard — permission-based route guard using custom auth_* tables.
 *
 * Uses route data:
 * { module: 'salary', resource: 'employee', action: 'view' }
 * OR fallback legacy:
 * { permissions: ['salary:read'] }
 *
 * Optional `siblingRoutes: () => Route[]` marks a landing route: when it is
 * denied, the user is sent to the first sibling page they can open (instead of
 * the "access denied" popup) and only gets the popup if there is none.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {
  private permissionService = inject(PermissionService);
  private keycloakService = inject(KeycloakService);
  private router = inject(Router);
  private notify = inject(NotifyService);
  private i18n = inject(I18nService);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const requiredModule = route.data?.['module'];
    const requiredResource = route.data?.['resource'];
    const requiredAction = route.data?.['action'];
    const required: string[] = route.data?.['permissions'] ?? [];

    // Wait for authoritative context from backend auth_* tables / Redis cache
    return this.keycloakService.userContext$.pipe(
      filter((ctx: any) => !!ctx && ctx.isAuthoritative === true),
      take(1),
      map(() => {
        // No permissions required — allow access
        if (!requiredModule && !requiredResource && required.length === 0) {
          return true;
        }

        let hasAccess = false;

        if (requiredModule && requiredResource && requiredAction) {
          hasAccess = this.permissionService.hasActionPermission(requiredModule, requiredResource, requiredAction);
        } else if (requiredModule && requiredResource) {
          hasAccess = this.permissionService.hasResourceAccess(requiredModule, requiredResource);
        } else if (requiredModule) {
          hasAccess = this.permissionService.hasModuleAccess(requiredModule);
        } else if (required.length > 0) {
          hasAccess = this.permissionService.canAny(required);
        }

        if (hasAccess) {
          return true;
        }

        const fallback = this.firstAccessibleUrl(route);
        if (fallback) {
          return fallback;
        }

        // Show a popup explaining the denial instead of navigating to a
        // dedicated /unauthorized page, then send the user home — every
        // route this guard protects gets the popup treatment globally.
        this.showAccessDenied(route.data?.['title'] ?? route.data?.['breadcrumb'] ?? requiredModule);
        return this.router.createUrlTree(['/modules']);
      })
    );
  }

  private firstAccessibleUrl(route: ActivatedRouteSnapshot): UrlTree | null {
    const siblingRoutes = route.data?.['siblingRoutes'];
    if (typeof siblingRoutes !== 'function') {
      return null;
    }
    const base = (route.pathFromRoot ?? []).flatMap((r) => r.url.map((u) => u.path));
    const rel = findFirstAccessiblePath(siblingRoutes(), (m, r) => this.permissionService.hasResourceAccess(m, r));
    return rel ? this.router.createUrlTree(['/', ...base, ...rel]) : null;
  }

  private showAccessDenied(name?: string): void {
    const message = name
      ? this.i18n.instant('common.accessDenied.pageMessageNamed', { name })
      : this.i18n.instant('common.accessDenied.pageMessage');
    const hint = this.i18n.instant('moduleSelector.contactHint');
    this.notify.warning(`${message} ${hint}`, {
      title: this.i18n.instant('common.accessDenied.title'),
      icon: 'lock',
    });
  }
}
